/**
 * Staff Profile Data Model
 * HR module for aged care staff management
 * Tracks credentials, training, qualifications, and compliance
 */

export const STAFF_ROLES = {
  RN: { code: 'RN', name: 'Registered Nurse', requiresAHPRA: true, careMinuteCategory: 'rn' },
  EN: { code: 'EN', name: 'Enrolled Nurse', requiresAHPRA: true, careMinuteCategory: 'en' },
  PCA: { code: 'PCA', name: 'Personal Care Assistant', requiresAHPRA: false, careMinuteCategory: 'pca' },
  AIN: { code: 'AIN', name: 'Assistant in Nursing', requiresAHPRA: false, careMinuteCategory: 'pca' },
  PHYSIO: { code: 'PHYSIO', name: 'Physiotherapist', requiresAHPRA: true, careMinuteCategory: 'allied' },
  OT: { code: 'OT', name: 'Occupational Therapist', requiresAHPRA: true, careMinuteCategory: 'allied' },
  SP: { code: 'SP', name: 'Speech Pathologist', requiresAHPRA: true, careMinuteCategory: 'allied' },
  DIETITIAN: { code: 'DIETITIAN', name: 'Dietitian', requiresAHPRA: true, careMinuteCategory: 'allied' },
  SW: { code: 'SW', name: 'Social Worker', requiresAHPRA: false, careMinuteCategory: 'allied' },
  LSO: { code: 'LSO', name: 'Lifestyle Officer', requiresAHPRA: false, careMinuteCategory: 'other' },
  FM: { code: 'FM', name: 'Facility Manager', requiresAHPRA: false, careMinuteCategory: 'other' },
  ADMIN: { code: 'ADMIN', name: 'Administration', requiresAHPRA: false, careMinuteCategory: 'other' }
};

export const MANDATORY_TRAINING = [
  { id: 'manual_handling', name: 'Manual Handling', frequency: 'annual', durationHours: 4 },
  { id: 'infection_control', name: 'Infection Control', frequency: 'annual', durationHours: 3 },
  { id: 'dementia_care', name: 'Dementia Care', frequency: 'annual', durationHours: 4 },
  { id: 'elder_abuse', name: 'Elder Abuse Recognition', frequency: 'annual', durationHours: 2 },
  { id: 'medication_competency', name: 'Medication Competency', frequency: 'annual', durationHours: 4 },
  { id: 'fire_safety', name: 'Fire Safety', frequency: 'annual', durationHours: 2 },
  { id: 'cpr_first_aid', name: 'CPR / First Aid', frequency: 'triennial', durationHours: 8 },
  { id: 'food_safety', name: 'Food Safety', frequency: 'triennial', durationHours: 4 },
  { id: 'falls_prevention', name: 'Falls Prevention', frequency: 'annual', durationHours: 2 },
  { id: 'wound_management', name: 'Wound Management', frequency: 'annual', durationHours: 3 },
  { id: 'palliative_care', name: 'Palliative Care', frequency: 'biennial', durationHours: 4 },
  { id: 'cultural_safety', name: 'Cultural Safety & Diversity', frequency: 'annual', durationHours: 2 }
];

export const EXPIRY_ALERT_THRESHOLDS = [
  { days: 60, severity: 'info', label: '60-day warning' },
  { days: 30, severity: 'warning', label: '30-day warning' },
  { days: 7, severity: 'critical', label: '7-day warning' },
  { days: 0, severity: 'expired', label: 'Expired' }
];

export const createStaffProfile = (data = {}) => {
  const now = new Date().toISOString();
  return {
    id: data.id || `staff-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,

    // Personal Details
    firstName: data.firstName || '',
    lastName: data.lastName || '',
    preferredName: data.preferredName || '',
    dateOfBirth: data.dateOfBirth || null,
    gender: data.gender || null,
    email: data.email || '',
    phone: data.phone || '',
    address: data.address || { street: '', suburb: '', state: '', postcode: '' },
    emergencyContact: data.emergencyContact || { name: '', relationship: '', phone: '', email: '' },
    photo: data.photo || null,

    // Employment
    employeeNumber: data.employeeNumber || `EMP${Date.now().toString().slice(-6)}`,
    facilityId: data.facilityId || null,
    facilityName: data.facilityName || '',
    department: data.department || null,
    roleCode: data.roleCode || 'PCA',
    roleName: data.roleName || STAFF_ROLES[data.roleCode]?.name || '',
    employmentType: data.employmentType || 'permanent', // permanent | casual | contract | agency
    employmentStatus: data.employmentStatus || 'full_time', // full_time | part_time | casual
    startDate: data.startDate || null,
    endDate: data.endDate || null,
    status: data.status || 'active', // active | on_leave | suspended | terminated

    // AHPRA Registration
    ahpra: data.ahpra || {
      registrationNumber: '',
      registrationType: '', // general | specialist | student
      division: '',
      expiryDate: null,
      verifiedDate: null,
      verifiedBy: null,
      status: 'not_applicable' // current | expired | not_applicable
    },

    // Worker Screening
    workerScreening: data.workerScreening || {
      checkDate: null,
      expiryDate: null,
      clearanceNumber: '',
      status: 'pending', // cleared | pending | expired | failed
      type: 'aged_care' // aged_care | disability | both
    },

    // Police Check
    policeCheck: data.policeCheck || {
      checkDate: null,
      expiryDate: null,
      referenceNumber: '',
      status: 'pending', // cleared | pending | expired | issues_noted
      issuingState: 'VIC'
    },

    // Right to Work
    rightToWork: data.rightToWork || {
      verified: false,
      verifiedDate: null,
      documentType: null, // passport | visa | birth_certificate | citizenship
      documentNumber: '',
      expiryDate: null,
      restrictions: ''
    },

    // Vaccination Records
    vaccinations: data.vaccinations || {
      influenza: { date: null, expiryDate: null, batchNumber: '', status: 'pending' },
      covid19: { dose1Date: null, dose2Date: null, boosterDate: null, status: 'pending' },
      hepatitisB: { date: null, status: 'pending' },
      other: []
    },

    // Qualifications
    qualifications: data.qualifications || [],

    // Mandatory Training
    trainingRecords: data.trainingRecords || MANDATORY_TRAINING.map(t => ({
      trainingId: t.id,
      trainingName: t.name,
      completedDate: null,
      expiryDate: null,
      score: null,
      certificateNumber: '',
      status: 'not_completed',
      frequency: t.frequency
    })),

    // Competency Assessments
    competencyAssessments: data.competencyAssessments || [],

    // Rostering Preferences
    rosterPreferences: data.rosterPreferences || {
      preferredShifts: [], // ['AM', 'PM', 'Night']
      unavailableDays: [],
      maxHoursPerWeek: 38,
      maxConsecutiveDays: 5,
      preferredWards: []
    },

    // Audit
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now,
    createdBy: data.createdBy || null,
    updatedBy: data.updatedBy || null
  };
};

export const getCredentialAlerts = (staffProfile) => {
  const alerts = [];
  const now = new Date();

  const checkExpiry = (label, expiryDate, category) => {
    if (!expiryDate) return;
    const expiry = new Date(expiryDate);
    const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));

    for (const threshold of EXPIRY_ALERT_THRESHOLDS) {
      if (daysUntilExpiry <= threshold.days) {
        alerts.push({
          staffId: staffProfile.id,
          staffName: `${staffProfile.firstName} ${staffProfile.lastName}`,
          credential: label,
          category,
          expiryDate,
          daysUntilExpiry: Math.max(0, daysUntilExpiry),
          severity: threshold.severity,
          message: daysUntilExpiry <= 0
            ? `${label} has expired`
            : `${label} expires in ${daysUntilExpiry} days`,
          blockRostering: daysUntilExpiry <= 0
        });
        break;
      }
    }
  };

  if (staffProfile.ahpra?.expiryDate) {
    checkExpiry('AHPRA Registration', staffProfile.ahpra.expiryDate, 'registration');
  }
  if (staffProfile.workerScreening?.expiryDate) {
    checkExpiry('Worker Screening', staffProfile.workerScreening.expiryDate, 'screening');
  }
  if (staffProfile.policeCheck?.expiryDate) {
    checkExpiry('Police Check', staffProfile.policeCheck.expiryDate, 'screening');
  }
  if (staffProfile.rightToWork?.expiryDate) {
    checkExpiry('Right to Work', staffProfile.rightToWork.expiryDate, 'employment');
  }
  if (staffProfile.vaccinations?.influenza?.expiryDate) {
    checkExpiry('Influenza Vaccination', staffProfile.vaccinations.influenza.expiryDate, 'vaccination');
  }

  (staffProfile.trainingRecords || []).forEach(t => {
    if (t.expiryDate) {
      checkExpiry(`Training: ${t.trainingName}`, t.expiryDate, 'training');
    }
  });

  return alerts.sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
};

export const isEligibleForRostering = (staffProfile) => {
  const alerts = getCredentialAlerts(staffProfile);
  const blockingAlerts = alerts.filter(a => a.blockRostering);

  if (staffProfile.status !== 'active') {
    return { eligible: false, reason: `Staff status is ${staffProfile.status}` };
  }
  if (blockingAlerts.length > 0) {
    return { eligible: false, reason: `Expired credentials: ${blockingAlerts.map(a => a.credential).join(', ')}`, blockingAlerts };
  }

  const roleConfig = STAFF_ROLES[staffProfile.roleCode];
  if (roleConfig?.requiresAHPRA && staffProfile.ahpra?.status !== 'current') {
    return { eligible: false, reason: 'AHPRA registration not current' };
  }

  return { eligible: true, reason: null };
};

export default {
  createStaffProfile,
  getCredentialAlerts,
  isEligibleForRostering,
  STAFF_ROLES,
  MANDATORY_TRAINING,
  EXPIRY_ALERT_THRESHOLDS
};
